import { gql, useMutation } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useFetchConnectedIntegrations } from '@modules/integrationsV2/hooks';
import { Box, Stack, Typography } from '@mui/material';
import {
  GetTenantByIdDocument,
  GetUserByEmailDocument,
  Query,
  SubmitFileUploadForCustomGroupRequest,
} from 'generated/graphql';
import { useGetUser } from 'v2/core/services/user';

const SUBMIT_FILE_UPLOAD_FOR_CUSTOM_GROUP = gql`
  mutation submitFileUploadForCustomGroup(
    $tenantID: ID!
    $integrationID: String!
    $url: String!
  ) {
    submitFileUploadForCustomGroup(
      request: { tenantID: $tenantID, integrationID: $integrationID, url: $url }
    ) {
      uploaded
    }
  }
`;

interface SubmitFileUploadForCohortGroupOptions {
  integrationID: string;
  fileURL: string;
}

interface UseSubmitFileUploadForCohortGroupOptions {
  onCompleted?: () => void;
  isReupload: boolean;
  integrationID: string;
}

export const useSubmitFileUploadForCohortGroup = ({
  integrationID,
  onCompleted,
  isReupload,
}: UseSubmitFileUploadForCohortGroupOptions) => {
  const {
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification, showSuccessNotification } =
    useNotificationContext();

  const { connectedIntegrations } = useFetchConnectedIntegrations({
    fetchPolicy: 'cache-first', // We already have the data in the cache, so we don't need to refetch it
  });

  const [submitFileUploadForCustomGroupMutation, { loading }] = useMutation<
    Query,
    SubmitFileUploadForCustomGroupRequest
  >(SUBMIT_FILE_UPLOAD_FOR_CUSTOM_GROUP, {
    notifyOnNetworkStatusChange: true,
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to submit file upload for cohort',
        props: {},
      });
    },
    onCompleted: () => {
      const integrationName = connectedIntegrations.find(
        (integration) => integration.id === integrationID,
      )?.namespace;

      showSuccessNotification({
        contentComponent: () => (
          <Stack>
            <Box>
              <Typography>File upload successful!</Typography>
              {isReupload ? (
                <Typography>
                  New feedback records will start getting added to{' '}
                  {`"${integrationName}"` || 'the'} cohort in 60 mins.
                </Typography>
              ) : (
                <Typography>
                  {`"${integrationName}"` || 'The'} cohort will be available as
                  filter for querying in 60 mins.
                </Typography>
              )}
            </Box>
          </Stack>
        ),
        props: {},
      });

      onCompleted?.();
    },
    refetchQueries: [GetTenantByIdDocument, GetUserByEmailDocument],
  });

  const submitFileUploadForCustomGroup = async ({
    fileURL,
    integrationID,
  }: SubmitFileUploadForCohortGroupOptions) => {
    return await submitFileUploadForCustomGroupMutation({
      variables: {
        tenantID,
        url: fileURL,
        integrationID,
      },
    });
  };

  return [submitFileUploadForCustomGroup, { loading }] as const;
};
